import React, { useState, useEffect } from "react";
import moment from "moment";
import { FaTrash } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";

import {
  addContent,
  updateContent,
  getAvailableRooms,
  getAvailableHotels,
} from "../../../FIREBASECONFIG";

import Loader from "../utilities/Loader";

import "react-toastify/dist/ReactToastify.css";

const percentageCal = (percentage, amount) =>
  (Number(percentage) / 100) * Number(amount);

const AddReservation = ({
  reservation = null,
  role,
  userDetails,
  toggleForm,
}) => {
  //console.log("r", reservation);

  const initialFormData = {
    reservationType: "Unconfirmed Reservation",
    bookingSource: "TDCP",
    passport: "",
    guest: "",
    mobile: "",
    email: "",
    address: "",
    adults: 1,
    children: 0,
    checkIn: "",
    checkOut: "",
    noOfDays: 1,
    hotelId: "",
    rooms: [], // ✅ add this
    totalRate: 0,
    totalRateWithTax: 0,
    bookingNotes: "",
    groupBooking: false,
  };

  const [formData, setFormData] = useState(initialFormData);
  const [roomOptions, setRoomOptions] = useState([]);
  const [hotelOptions, setHotelOptions] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [fullLoading, setFullLoading] = useState(false);

  // Pre-fill for editing
  useEffect(() => {
    console.log("=====");
    const fetchRooms = async () => {
      // Use formData or reservation values
      const hotelId = formData.hotelId || reservation?.hotelId;
      const checkIn = formData.checkIn || reservation?.checkIn;
      const checkOut = formData.checkOut || reservation?.checkOut;

      if (!hotelId || !checkIn || !checkOut) return;

      setLoadingRooms(true);
      try {
        const rooms = await getAvailableRooms(hotelId, checkIn, checkOut);
        console.log("rooms : ", rooms);
        setRoomOptions(rooms || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingRooms(false);
      }
    };

    fetchRooms();
  }, [formData.hotelId, formData.checkIn, formData.checkOut, reservation]);

  useEffect(async () => {
    console.log(reservation, roomOptions);

    if (!reservation || !roomOptions.length) return;
    setFullLoading(true);
    console.log("h=>:", reservation);

    const updatedRooms = reservation.roomDetails.map((r) => {
      const room = roomOptions.find((ro) => ro.id === r.id);
      const ratePlan = room?.plans?.find((ro) => ro.id === r.planId);

      //console.log(room);
      return {
        roomId: r.id,
        name: r.name,
        ratePlanId: r.planId,
        ratePlan: r.planName,
        quantity: r.quantity || 1,
        rate: r.rate?.reduce((sum, x) => sum + Number(x.ratePerNight), 0) || 0,
        tax: r.rate?.reduce((sum, x) => sum + Number(x.taxAmount), 0) || 0,
        total: r.roomTotalPrice || 0,
        withTax: (r.roomTotalPrice || 0) + (r.roomTotalTax || 0),
        availablePlans: room?.plans || [], // populate available plans
        nightlyPrices: ratePlan.nightlyPrices || r.rate,
        guests: r.guests || reservation.adults,
      };
    });

    setFormData((prev) => ({
      ...prev,
      ...reservation,
      guest: reservation.firstName + " " + reservation.lastName,
      rooms: updatedRooms,
      totalRate: updatedRooms.reduce((a, r) => a + r.total, 0),
      totalRateWithTax: updatedRooms.reduce((a, r) => a + r.withTax, 0),
    }));
    setFullLoading(false);
  }, [reservation, roomOptions]);

  // Fetch available hotels
  useEffect(() => {
    const fetchHotels = async () => {
      if (!formData.checkIn || !formData.checkOut) return;
      try {
        let Hotels = await getAvailableHotels(
          formData.checkIn,
          formData.checkOut
        );
        if (role === "hoteladmin" && userDetails?.hotelId) {
          Hotels = Hotels.filter((h) => h.id === userDetails.hotelId);
        }
        setHotelOptions(
          Hotels.map((h) => ({
            label: h.name,
            value: h.id,
            rawData: h, // <-- keep full hotel data here
          }))
        );
      } catch (err) {
        console.error(err);
      }
    };
    fetchHotels();
  }, [formData.checkIn, formData.checkOut, role, userDetails]);

  // Handle general input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Add room
  const handleAddRoom = () => {
    if (!formData.hotelId) {
      toast.warning("Please select a hotel first!");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      rooms: [
        ...prev.rooms,
        {
          roomId: "",
          ratePlan: "",
          chargeType: "0",
          rate: 0,
          tax: 0,
          total: 0,
          withTax: 0,
        },
      ],
    }));
  };

  // Room change
  const handleRoomChange = (idx, field, value) => {
    console.log(formData);
    const updated = [...formData.rooms];
    updated[idx][field] = value;

    // If room type is selected → load its plans
    if (field === "roomId") {
      const selectedRoom = roomOptions.find((r) => r.id === value);
      console.log("Room", selectedRoom);
      if (selectedRoom) {
        updated[idx].availablePlans = selectedRoom.plans || [];
        updated[idx].ratePlanId = "";
        updated[idx].name = selectedRoom.name;
        updated[idx].ratePlan = "";
        updated[idx].rate = 0;
        updated[idx].quantity = 1;
        updated[idx].tax = 0;
        updated[idx].total = 0;
        updated[idx].withTax = 0;
      }
    }

    if (field === "quantity") {
      const rate = Number(updated[idx].rate || 0);
      const qty = Number(value || 1);
      const subtotal = rate * qty;

      // Get selected hotel
      const selectedHotel = hotelOptions.find(
        (h) => h.value === formData.hotel
      );
      const hotelData = selectedHotel?.rawData; // store raw hotel data when setting options
      const taxPercentage = hotelData?.taxes?.[0]?.percentage || 0;

      const tax = percentageCal(taxPercentage, subtotal);

      updated[idx].total = subtotal;
      updated[idx].tax = tax;
      updated[idx].withTax = subtotal + tax;
    }

    setFormData((prev) => ({
      ...prev,
      rooms: updated,
      totalRate: updated.reduce((a, r) => a + r.total, 0),
      totalRateWithTax: updated.reduce((a, r) => a + r.withTax, 0),
    }));
  };

  const handlePlanSelect = (idx, planId) => {
    const updated = [...formData.rooms];
    const selectedRoom = roomOptions.find((r) => r.id === updated[idx].roomId);
    if (!selectedRoom) return;

    const plan = selectedRoom.plans.find((p) => p.id === planId);
    if (!plan) return;

    updated[idx].ratePlanId = plan.id;
    updated[idx].ratePlan = plan.name;
    updated[idx].rate = Number(plan.price);

    const subtotal = updated[idx].rate * updated[idx].quantity;

    // Get selected hotel
    const selectedHotel = hotelOptions.find((h) => h.value === formData.hotel);
    const hotelData = selectedHotel?.rawData;
    console.log("h", hotelData);
    const taxPercentage = hotelData?.taxes?.[0]?.percentage || 0;

    const tax = percentageCal(taxPercentage, subtotal);

    updated[idx].tax = tax;
    updated[idx].total = subtotal;
    updated[idx].withTax = subtotal + tax;

    setFormData((prev) => ({
      ...prev,
      rooms: updated,
      totalRate: updated.reduce((a, r) => a + r.total, 0),
      totalRateWithTax: updated.reduce((a, r) => a + r.withTax, 0),
    }));
  };

  const handleDeleteRoom = (idx) => {
    const updated = [...formData.rooms];
    updated.splice(idx, 1);
    setFormData((prev) => ({
      ...prev,
      rooms: updated,
      totalRate: updated.reduce((a, r) => a + r.total, 0),
      totalRateWithTax: updated.reduce((a, r) => a + r.withTax, 0),
    }));
  };

  // Calculate number of days
  useEffect(() => {
    if (formData.checkIn && formData.checkOut) {
      const diff =
        (new Date(formData.checkOut) - new Date(formData.checkIn)) /
        (1000 * 60 * 60 * 24);
      setFormData((prev) => ({ ...prev, noOfDays: diff > 0 ? diff : 1 }));
    }
  }, [formData.checkIn, formData.checkOut]);

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    const selectedHotel = hotelOptions.find(
      (h) => h.value === formData.hotelId
    )?.rawData;

    // 🔹 Prepare roomDetails in correct format
    console.log("formData", formData);
    const roomDetails = formData.rooms.map((r) => {
      const selectedPricingArray =
        r.nightlyPrices?.map((n) => ({
          date: n.date,
          ratePerNight: n.price || 0,
          taxAmount: ((n.price || 0) * (r.tax || 0)) / (r.rate || 1), // approximate per night tax
        })) || [];

      console.log("selectedPricingArray", selectedPricingArray);
      return {
        guests: Number(r.guests || formData.adults),
        id: r.roomId,
        name: r.name || "",
        planId: r.ratePlanId,
        planName: r.ratePlan,
        quantity: Number(r.quantity || 1),
        rate: selectedPricingArray,
        roomTotalPrice: selectedPricingArray.reduce(
          (sum, a) => sum + Number(a.ratePerNight),
          0
        ),
        roomTotalTax: selectedPricingArray.reduce(
          (sum, a) => sum + Number(a.taxAmount),
          0
        ),
      };
    });

    // 🔹 Prepare final reservation object
    const newReservation = {
      address: formData.address,
      bookingDate: moment().format("YYYY-MM-D"),
      bookingId: new Date().getTime().toString(),
      checkIn: formData.checkIn,
      checkOut: formData.checkOut,
      noOfDays: formData.noOfDays,
      adults: Number(formData.adults),
      children: Number(formData.children || 0),
      checkInTime: formData.checkInTime || "",
      checkOutTime: formData.checkOutTime || "",
      city: selectedHotel?.city || "",
      isAscendant: false,
      discount: formData.discount || 0,
      bookingNotes: formData.bookingNotes || "",
      discountType: formData.discountType || "",
      source: formData.bookingSource,
      email: formData.email,
      employeeId: "",
      firstName: formData.guest.split(" ")[0] || "",
      hotelName: selectedHotel.name,
      hotelId: selectedHotel?.id || "",
      id: reservation?.id || new Date().getTime().toString(),
      image: selectedHotel?.image || "",
      lastName: formData.guest.split(" ")[1] || "",
      mobile: formData.mobile,
      name: selectedHotel?.name || "",
      roomDetails: roomDetails,
      status: "pending",
      reservationType: formData.reservationType,
      color: formData.color || "", // optional
      taxes:
        Number(formData.totalRateWithTax || 0) -
        Number(formData.totalRate || 0),
      totalPrice: Number(formData.totalRate || 0),
      userId: "",
      payProId: "",
      pinCode: new Date().getTime().toString(),
      passport: formData.passport || "",
    };
    console.log("new reservation:", newReservation);
    try {
      if (reservation) {
        await updateContent(
          "reservations",
          "",
          reservation.id,
          newReservation,
          null
        );
        toast.success("Reservation updated!");
        toggleForm();
      } else {
        await addContent("reservations", "", newReservation, null);
        toast.success("Reservation added!");
        //toggleForm();
      }
      setFormData(initialFormData);
    } catch (err) {
      toast.error("Failed to save reservation!");
      console.error(err);
    }
  };

  return (
    <div>
      <ToastContainer />
      <Loader loading={fullLoading || loadingRooms} />

      <form onSubmit={handleSubmit}>
        {/* Booking Type & Source */}
        <div className="row mb-3">
          <div className="col-6">
            <label>Booking Type *</label>
            <select
              className="form-select"
              name="reservationType"
              value={formData.reservationType}
              onChange={handleChange}
            >
              <option value="Reservation">Reservation</option>
              <option value="Check-in">Check-in</option>
              <option value="Check-out">Check-out</option>
              <option value="Cancelled">Cancelled (hide)</option>
              <option value="No-show">No-show</option>
              <option value="Unconfirmed Reservation">
                Unconfirmed Reservation
              </option>
            </select>
          </div>
          <div className="col-6">
            <label>Booking Source *</label>
            <select
              className="form-select"
              name="bookingSource"
              value={formData.bookingSource}
              onChange={handleChange}
            >
              <option value="TDCP">TDCP</option>
              <option value="Walk-in">Walk-in</option>
              <option value="mobile Call">mobile Call</option>
            </select>
          </div>
        </div>

        {/* Guest Info */}
        <div className="row mb-3">
          <div className="col-3">
            <label>CNIC/Passport</label>
            <input
              type="text"
              className="form-control"
              name="passport"
              value={formData.passport}
              onChange={handleChange}
            />
          </div>
          <div className="col-3">
            <label>Guest *</label>
            <input
              type="text"
              className="form-control"
              name="guest"
              value={formData.guest}
              onChange={handleChange}
              required
            />
          </div>
          <div className="col-3">
            <label>mobile *</label>
            <input
              type="tel"
              className="form-control"
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              required
            />
          </div>
          <div className="col-3">
            <label>Email</label>
            <input
              type="email"
              className="form-control"
              name="email"
              value={formData.email}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-6">
            <label>Address</label>
            <input
              type="text"
              className="form-control"
              name="address"
              value={formData.address}
              onChange={handleChange}
            />
          </div>
          <div className="col-3">
            <label>Adults *</label>
            <input
              type="number"
              className="form-control"
              name="adults"
              value={formData.adults}
              min={1}
              onChange={handleChange}
            />
          </div>
          <div className="col-3">
            <label>Children *</label>
            <input
              type="number"
              className="form-control"
              name="children"
              value={formData.children}
              min={0}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* Check-in/Check-out */}
        <div className="row mb-3">
          <div className="col-4">
            <label>Check-in *</label>
            <input
              type="date"
              className="form-control"
              name="checkIn"
              value={formData.checkIn}
              onChange={handleChange}
            />
          </div>
          <div className="col-4">
            <label>Check-out *</label>
            <input
              type="date"
              className="form-control"
              name="checkOut"
              value={formData.checkOut}
              onChange={handleChange}
            />
          </div>
          <div className="col-4">
            <label>No of Days</label>
            <input
              type="number"
              className="form-control"
              value={formData.noOfDays}
              readOnly
            />
          </div>
        </div>

        {/* Hotel */}
        <div className="mb-3">
          <label>Hotel *</label>
          <select
            className="form-select"
            name="hotelId"
            value={formData.hotelId}
            onChange={handleChange}
            disabled={!formData.checkIn || !formData.checkOut}
          >
            <option value="">-- Select Hotel --</option>
            {hotelOptions.map((h) => (
              <option key={h.value} value={h.value}>
                {h.label}
              </option>
            ))}
          </select>
        </div>

        {/* Rooms */}
        <div className="mb-3">
          <h5>Rooms</h5>

          {roomOptions.length ? (
            <>
              {formData.rooms.map((room, idx) => (
                <div key={idx} className="p-3 mb-3 border rounded">
                  {/* Row 1 */}
                  <div className="row mb-2">
                    <div className="col-2">
                      <label>Room Type *</label>
                      <select
                        className="form-select"
                        value={room.roomId}
                        onChange={(e) =>
                          handleRoomChange(idx, "roomId", e.target.value)
                        }
                      >
                        {roomOptions.length && (
                          <>
                            <option value="">-- Select --</option>
                            {roomOptions.map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.name}
                              </option>
                            ))}
                          </>
                        )}
                      </select>
                    </div>
                    {/* {console.log("s=>", room)} */}

                    <div className="col-2">
                      <label>Rate Plan *</label>
                      <select
                        className="form-select"
                        value={room.ratePlanId || ""}
                        onChange={(e) => handlePlanSelect(idx, e.target.value)}
                        disabled={!room.availablePlans?.length}
                      >
                        <option value="">-- Select Plan --</option>
                        {room.availablePlans?.map((plan) => (
                          <option key={plan.id} value={plan.id}>
                            {plan.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-2">
                      <label>Rate/Night</label>
                      <input
                        type="number"
                        className="form-control"
                        value={room.rate || 0}
                        readOnly
                      />
                    </div>

                    <div className="col-2">
                      <label>Tax/Night</label>
                      <input
                        type="number"
                        className="form-control"
                        value={room.tax || 0}
                        readOnly
                      />
                    </div>

                    <div className="col-2">
                      <label>Quantity</label>
                      <input
                        type="number"
                        min="1"
                        className="form-control"
                        value={room.quantity || 1}
                        onChange={(e) =>
                          handleRoomChange(idx, "quantity", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-2 d-flex align-items-center">
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => handleDeleteRoom(idx)}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>

                  {/* Row 2 */}
                </div>
              ))}
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={handleAddRoom}
              >
                + Add Room
              </button>
            </>
          ) : (
            <div>No Rooms Found</div>
          )}
        </div>

        {/* Total */}
        <div className="row mb-3">
          <div className="col-6">
            <label>Total Rate</label>
            <input
              type="number"
              className="form-control"
              value={formData.totalRate}
              readOnly
            />
          </div>
          <div className="col-6">
            <label>Total Rate (with tax)</label>
            <input
              type="number"
              className="form-control"
              value={formData.totalRateWithTax}
              readOnly
            />
          </div>
        </div>

        {/* Booking Notes */}
        <div className="mb-3">
          <label>Booking Notes</label>
          <textarea
            className="form-control"
            name="bookingNotes"
            rows={4}
            value={formData.bookingNotes}
            onChange={handleChange}
          />
        </div>

        <button type="submit" className="btn btn-primary me-2">
          Save
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={toggleForm}
        >
          Cancel
        </button>
      </form>
    </div>
  );
};

export default AddReservation;
